import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Power, Save, UploadCloud, RotateCcw, Undo2, Redo2, Copy, Trash2, Plus, Play,
  Monitor, Tablet, Smartphone, Sparkles, Wind, Sun, Volume2, CalendarClock, Wand2, Film,
} from "lucide-react";
import { toast } from "sonner";
import { AnimationPlayer } from "../landing/AnimationPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OCCASIONS = ["halloween", "christmas", "newyear", "valentine", "swiss", "custom"];
const FREQ = [
  { id: "every", label: "Every visit" },
  { id: "session", label: "Once per session" },
  { id: "day", label: "Once per day" },
  { id: "first", label: "First visit only" },
];
const DIRECTIONS = [
  { id: "ltr", label: "Left → Right", geo: { startX: -8, startY: 50, endX: 108, endY: 50 } },
  { id: "rtl", label: "Right → Left", geo: { startX: 108, startY: 50, endX: -8, endY: 50 } },
  { id: "ttb", label: "Top → Bottom", geo: { startX: 50, startY: -8, endX: 50, endY: 108 } },
  { id: "btt", label: "Bottom → Top", geo: { startX: 50, startY: 108, endX: 50, endY: -8 } },
  { id: "diagonal", label: "Diagonal", geo: { startX: 8, startY: 12, endX: 98, endY: 88 } },
  { id: "custom", label: "Custom path", geo: null },
];
const NOMINAL = { desktop: 1440, tablet: 834, mobile: 390 };

const lbl = "text-[11px] font-bold uppercase tracking-wide text-amber-200/70";
const fieldCls = "w-full rounded-lg bg-black/40 border border-amber-500/20 px-3 py-2 text-sm text-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-400/40";
const goldBtn = "inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 px-4 py-2.5 text-sm font-bold text-black shadow-lg shadow-amber-500/20 hover:brightness-110 disabled:opacity-40 transition";
const ghostBtn = "inline-flex items-center gap-2 rounded-xl border border-amber-500/25 bg-black/30 px-3 py-2 text-sm font-semibold text-amber-100 hover:border-amber-400/60 disabled:opacity-40 transition";

const detectType = (file, url) => {
  const ct = file?.type || "";
  const name = (file?.name || url || "").toLowerCase();
  if (ct.startsWith("video/") || /\.(mp4|webm|mov)$/.test(name)) return name.endsWith(".webm") ? "webm" : "mp4";
  if (ct.includes("json") || /\.(json|lottie)$/.test(name)) return "lottie";
  return "image";
};

const Toggle = ({ on, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`relative h-7 w-12 rounded-full transition ${on ? "bg-amber-400" : "bg-slate-600"}`}
    aria-pressed={on}
  >
    <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

export const SeasonalAnimationTab = () => {
  const [live, setLive] = useState(null);
  const [draft, setDraftRaw] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [selectedId, setSelectedId] = useState("");
  const [preview, setPreview] = useState("desktop");
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const past = useRef([]);
  const future = useRef([]);
  const stageRef = useRef(null);
  const [stageW, setStageW] = useState(900);
  const fileRef = useRef(null);
  const soundRef = useRef(null);
  const saveTimer = useRef(null);

  const device = preview === "mobile" ? "mobile" : "desktop";
  const selected = draft?.presets?.find((p) => p.id === selectedId) || null;

  /* ---------- load ---------- */
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/seasonal-animation/full`, { withCredentials: true });
    setLive(data.live);
    const d = data.draft || structuredClone(data.live);
    setDraftRaw(d);
    setHistoryCount(data.history_count || 0);
    setSelectedId((prev) => prev || d.active_preset_id || d.presets?.[0]?.id || "");
    past.current = [];
    future.current = [];
    setDirty(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setStageW(el.clientWidth));
    ro.observe(el);
    setStageW(el.clientWidth);
    return () => ro.disconnect();
  }, [draft, preview]);

  /* ---------- draft mutation with undo/redo ---------- */
  const setDraft = useCallback((updater) => {
    setDraftRaw((prev) => {
      if (prev) { past.current.push(structuredClone(prev)); if (past.current.length > 60) past.current.shift(); }
      future.current = [];
      return typeof updater === "function" ? updater(structuredClone(prev)) : updater;
    });
    setDirty(true);
  }, []);
  const undo = () => {
    if (!past.current.length) return;
    setDraftRaw((prev) => { future.current.push(structuredClone(prev)); return past.current.pop(); });
    setDirty(true);
  };
  const redo = () => {
    if (!future.current.length) return;
    setDraftRaw((prev) => { past.current.push(structuredClone(prev)); return future.current.pop(); });
    setDirty(true);
  };

  /* ---------- autosave ---------- */
  useEffect(() => {
    if (!draft || !dirty) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/seasonal-animation/draft`, draft, { withCredentials: true });
        setDirty(false);
      } catch { /* retry on next change */ }
    }, 900);
    return () => clearTimeout(saveTimer.current);
  }, [draft, dirty]);

  /* ---------- preset helpers ---------- */
  const patchPreset = (patch) =>
    setDraft((d) => ({ ...d, presets: d.presets.map((p) => (p.id === selectedId ? { ...p, ...patch } : p)) }));
  const patchNested = (key, patch) =>
    setDraft((d) => ({ ...d, presets: d.presets.map((p) => (p.id === selectedId ? { ...p, [key]: { ...p[key], ...patch } } : p)) }));
  const patchGeo = (patch) =>
    setDraft((d) => ({ ...d, presets: d.presets.map((p) => (p.id === selectedId ? { ...p, [device]: { ...p[device], ...patch } } : p)) }));

  const activate = (id) => setDraft((d) => ({ ...d, active_preset_id: id }));
  const duplicatePreset = (id) => {
    const p = draft.presets.find((x) => x.id === id);
    const copy = structuredClone(p);
    copy.id = `p_${Date.now().toString(36)}`;
    copy.name = `${p.name} (copy)`;
    setDraft((d) => ({ ...d, presets: [...d.presets, copy] }));
    setSelectedId(copy.id);
    toast.success("Preset duplicated");
  };
  const deletePreset = (id) => {
    if (draft.presets.length <= 1) return toast.error("Keep at least one preset");
    setDraft((d) => {
      const presets = d.presets.filter((p) => p.id !== id);
      const active = d.active_preset_id === id ? presets[0]?.id : d.active_preset_id;
      return { ...d, presets, active_preset_id: active };
    });
    if (selectedId === id) setSelectedId(draft.presets.find((p) => p.id !== id)?.id || "");
    toast.success("Preset deleted");
  };
  const addPreset = () => {
    const np = {
      id: `p_${Date.now().toString(36)}`,
      name: "New Animation", occasion: "custom",
      asset: { url: "", type: "image" },
      duration: 5, opacity: 1, entrance: "fade", exit: "fade", direction: "diagonal",
      effects: { glow: 0.6, glow_color: "#ffaa00", smoke: true, sparkles: true, sparkle_color: "#ffffff", shadow: true },
      sound: { enabled: false, url: "", volume: 0.5 },
      pages: "home", devices: "all",
      desktop: { startX: 8, startY: 12, endX: 98, endY: 88, width: 420, rotation: 0, path: [] },
      mobile: { startX: 18, startY: 16, endX: 86, endY: 82, width: 220, rotation: 0, path: [] },
    };
    setDraft((d) => ({ ...d, presets: [...d.presets, np] }));
    setSelectedId(np.id);
  };

  const uploadAsset = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return toast.error("Max file size is 50 MB");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/media`, fd, { withCredentials: true });
      patchNested("asset", { url: data.url, type: detectType(file, data.url) });
      toast.success("Asset uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };
  const uploadSound = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/media`, fd, { withCredentials: true });
      patchNested("sound", { url: data.url, enabled: true });
      toast.success("Sound uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const applyDirection = (dirId) => {
    const dir = DIRECTIONS.find((d) => d.id === dirId);
    if (dirId === "custom") {
      const g = selected[device];
      patchGeo({ path: [{ x: g.startX, y: g.startY }, { x: (g.startX + g.endX) / 2, y: (g.startY + g.endY) / 2 }, { x: g.endX, y: g.endY }] });
      patchPreset({ direction: "custom" });
    } else {
      patchGeo({ ...dir.geo, path: [] });
      patchPreset({ direction: dirId });
    }
  };

  /* ---------- publishing ---------- */
  const saveDraft = async () => {
    setSaving(true);
    try { await axios.put(`${API}/seasonal-animation/draft`, draft, { withCredentials: true }); setDirty(false); toast.success("Draft saved"); }
    finally { setSaving(false); }
  };
  const publish = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/seasonal-animation/draft`, draft, { withCredentials: true });
      await axios.post(`${API}/seasonal-animation/publish`, {}, { withCredentials: true });
      toast.success("Published — now live on the website");
      await load();
    } finally { setSaving(false); }
  };
  const revert = async () => {
    setSaving(true);
    try { await axios.post(`${API}/seasonal-animation/revert`, {}, { withCredentials: true }); toast.success("Reverted to previous published version"); await load(); }
    catch { toast.error("No previous version to restore"); }
    finally { setSaving(false); }
  };
  const resetToLive = () => { setDraftRaw(structuredClone(live)); past.current = []; future.current = []; setDirty(false); toast.message("Draft reset to published version"); };
  const toggleMaster = async () => {
    const next = !live.enabled;
    try {
      const { data } = await axios.post(`${API}/seasonal-animation/toggle`, { enabled: next }, { withCredentials: true });
      setLive(data.live);
      setDraftRaw((d) => ({ ...d, enabled: next }));
      toast.success(next ? "Animation turned ON (live)" : "Animation turned OFF (live)");
    } catch { toast.error("Could not toggle"); }
  };

  /* ---------- visual editor drag ---------- */
  const dragState = useRef(null);
  const pointFromEvent = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    const cx = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
    const cy = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
    return { x: Math.max(-10, Math.min(110, (cx / r.width) * 100)), y: Math.max(-10, Math.min(110, (cy / r.height) * 100)) };
  };
  const startDrag = (kind, idx) => (e) => { e.preventDefault(); e.stopPropagation(); dragState.current = { kind, idx }; };
  const onStageMove = (e) => {
    if (!dragState.current || !selected) return;
    const { kind, idx } = dragState.current;
    const p = pointFromEvent(e);
    if (kind === "start") patchGeo({ startX: Math.round(p.x), startY: Math.round(p.y) });
    else if (kind === "end") patchGeo({ endX: Math.round(p.x), endY: Math.round(p.y) });
    else if (kind === "path") {
      const path = [...(selected[device].path || [])];
      path[idx] = { x: Math.round(p.x), y: Math.round(p.y) };
      patchGeo({ path });
    }
  };
  const endDrag = () => { dragState.current = null; };

  if (!draft || !selected) {
    return <div className="rounded-3xl bg-[#0b0b0f] border border-amber-500/20 p-10 text-center text-amber-200/60">Loading Animation Studio…</div>;
  }

  const geo = selected[device] || {};
  const usesPath = selected.direction === "custom" && (geo.path?.length >= 2);
  const scale = stageW / NOMINAL[preview];
  const stageMax = preview === "desktop" ? "100%" : `${NOMINAL[preview]}px`;
  const thumbUrl = (p) => p.asset?.url && !["webm", "mp4", "video", "lottie"].includes(p.asset?.type) ? p.asset.url : null;

  return (
    <div className="rounded-3xl bg-[#0b0b0f] border border-amber-500/20 p-5 sm:p-7 text-amber-50" data-testid="seasonal-animation-studio">
      {/* ===== Header / publishing ===== */}
      <div className="flex flex-wrap items-center gap-4 justify-between border-b border-amber-500/15 pb-5">
        <div>
          <div className="flex items-center gap-2 text-xl font-black tracking-tight">
            <Wand2 className="w-5 h-5 text-amber-400" /> Seasonal Animation Studio
          </div>
          <p className="text-sm text-amber-200/60 mt-1">One cinematic character flies across your site, then disappears. Switch ON to go live.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-amber-200/50">Master switch</div>
            <div className="flex items-center gap-2 mt-0.5">
              <Power className={`w-4 h-4 ${live.enabled ? "text-amber-400" : "text-slate-500"}`} />
              <Toggle on={live.enabled} onClick={toggleMaster} testid="anim-master-toggle" />
              <span className="text-sm font-bold">{live.enabled ? "ON" : "OFF"}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 py-4">
        <button className={ghostBtn} onClick={undo} disabled={!past.current.length} data-testid="anim-undo"><Undo2 className="w-4 h-4" /> Undo</button>
        <button className={ghostBtn} onClick={redo} disabled={!future.current.length} data-testid="anim-redo"><Redo2 className="w-4 h-4" /> Redo</button>
        <button className={ghostBtn} onClick={resetToLive} data-testid="anim-reset"><RotateCcw className="w-4 h-4" /> Reset</button>
        <div className="flex-1" />
        <span className="text-xs text-amber-200/50">{dirty ? "Unsaved changes…" : "All changes saved"}</span>
        <button className={ghostBtn} onClick={saveDraft} disabled={saving} data-testid="anim-save-draft"><Save className="w-4 h-4" /> Save draft</button>
        <button className={ghostBtn} onClick={revert} disabled={saving || !historyCount} data-testid="anim-revert">Restore previous</button>
        <button className={goldBtn} onClick={publish} disabled={saving} data-testid="anim-publish">Publish now</button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[260px_1fr] gap-6">
        {/* ===== A. Library ===== */}
        <div className="space-y-3" data-testid="anim-library">
          <div className="flex items-center justify-between">
            <span className={lbl}>Animation Library</span>
            <button className="text-amber-300 hover:text-amber-200" onClick={addPreset} data-testid="anim-add-preset" title="Upload / new"><Plus className="w-5 h-5" /></button>
          </div>
          <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
            {draft.presets.map((p) => (
              <div
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                data-testid={`anim-preset-${p.id}`}
                className={`rounded-xl border p-2.5 cursor-pointer transition ${selectedId === p.id ? "border-amber-400 bg-amber-400/10" : "border-amber-500/15 bg-black/30 hover:border-amber-400/40"}`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="h-12 w-16 shrink-0 rounded-lg bg-black/50 border border-white/5 overflow-hidden flex items-center justify-center">
                    {thumbUrl(p) ? <img src={p.asset.url} alt="" className="max-h-full max-w-full object-contain" /> : <Film className="w-4 h-4 text-amber-200/40" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold truncate">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-wide text-amber-200/50">{p.occasion}</div>
                  </div>
                  {draft.active_preset_id === p.id && <span className="text-[9px] font-black text-black bg-amber-400 rounded px-1.5 py-0.5">ACTIVE</span>}
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <button onClick={(e) => { e.stopPropagation(); activate(p.id); }} className="flex-1 text-[11px] font-bold rounded-md bg-amber-400/15 text-amber-200 py-1 hover:bg-amber-400/25" data-testid={`anim-activate-${p.id}`}>Set active</button>
                  <button onClick={(e) => { e.stopPropagation(); duplicatePreset(p.id); }} className="p-1.5 rounded-md hover:bg-white/10" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); deletePreset(p.id); }} className="p-1.5 rounded-md hover:bg-red-500/20 text-red-300" title="Delete" data-testid={`anim-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== Right column ===== */}
        <div className="space-y-6 min-w-0">
          {/* Visual editor */}
          <div className="rounded-2xl border border-amber-500/15 bg-black/20 p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <span className={lbl}>Visual Editor — drag the character</span>
              <div className="flex items-center gap-1.5">
                {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([m, Icon]) => (
                  <button key={m} onClick={() => setPreview(m)} className={`p-2 rounded-lg border ${preview === m ? "border-amber-400 bg-amber-400/15 text-amber-200" : "border-amber-500/20 text-amber-200/50"}`} data-testid={`anim-preview-${m}`}><Icon className="w-4 h-4" /></button>
                ))}
                <button onClick={() => { setPlaying(false); requestAnimationFrame(() => setPlaying(true)); }} className={`${goldBtn} ml-2`} data-testid="anim-play"><Play className="w-4 h-4" /> Play</button>
              </div>
            </div>
            <div className="mx-auto transition-all" style={{ maxWidth: stageMax }}>
              <div
                ref={stageRef}
                onMouseMove={onStageMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                onTouchMove={onStageMove}
                onTouchEnd={endDrag}
                className="relative w-full rounded-xl overflow-hidden border border-white/10 bg-slate-900 select-none"
                style={{ aspectRatio: preview === "mobile" ? "9 / 16" : "16 / 9" }}
                data-testid="anim-editor-stage"
              >
                <iframe title="site-preview" src={`${window.location.origin}/?anim_edit=1`} className="absolute inset-0 w-full h-full pointer-events-none" style={{ border: 0 }} />
                {!playing && (
                  <>
                    {/* start marker */}
                    <div
                      onMouseDown={startDrag("start")}
                      onTouchStart={startDrag("start")}
                      className="absolute -translate-x-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing"
                      style={{ left: `${geo.startX}%`, top: `${geo.startY}%`, width: Math.max(40, geo.width * scale) }}
                      data-testid="anim-start-marker"
                    >
                      {thumbUrl(selected) ? <img src={selected.asset.url} alt="" className="w-full pointer-events-none" draggable={false} /> : <div className="w-full aspect-[3/2] rounded-lg bg-amber-400/20 border border-dashed border-amber-300/50" />}
                      <span className="absolute -top-5 left-0 text-[10px] font-bold text-emerald-300 bg-black/70 px-1.5 rounded">START</span>
                    </div>
                    {/* end marker */}
                    {!usesPath && (
                      <div
                        onMouseDown={startDrag("end")}
                        onTouchStart={startDrag("end")}
                        className="absolute -translate-x-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing opacity-60"
                        style={{ left: `${geo.endX}%`, top: `${geo.endY}%`, width: Math.max(32, geo.width * scale * 0.7) }}
                        data-testid="anim-end-marker"
                      >
                        {thumbUrl(selected) ? <img src={selected.asset.url} alt="" className="w-full pointer-events-none" draggable={false} /> : <div className="w-full aspect-[3/2] rounded-lg bg-rose-400/20 border border-dashed border-rose-300/50" />}
                        <span className="absolute -top-5 left-0 text-[10px] font-bold text-rose-300 bg-black/70 px-1.5 rounded">END</span>
                      </div>
                    )}
                    {/* custom path points */}
                    {usesPath && geo.path.map((pt, i) => (
                      <div key={i} onMouseDown={startDrag("path", i)} onTouchStart={startDrag("path", i)} className="absolute -translate-x-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-amber-400 border-2 border-black cursor-grab active:cursor-grabbing" style={{ left: `${pt.x}%`, top: `${pt.y}%` }} data-testid={`anim-path-point-${i}`}>
                        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-amber-200">{i + 1}</span>
                      </div>
                    ))}
                  </>
                )}
                {playing && <AnimationPlayer preset={selected} device={device} inline previewScale={scale} onComplete={() => setPlaying(false)} />}
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <label className="text-xs text-amber-200/70">Size
                <input type="range" min="80" max="900" value={geo.width} onChange={(e) => patchGeo({ width: Number(e.target.value) })} className="ml-2 align-middle" data-testid="anim-size-slider" />
                <span className="ml-1">{geo.width}px</span>
              </label>
              <label className="text-xs text-amber-200/70 ml-auto">Rotation
                <input type="range" min="-45" max="45" value={geo.rotation || 0} onChange={(e) => patchGeo({ rotation: Number(e.target.value) })} className="ml-2 align-middle" />
                <span className="ml-1">{geo.rotation || 0}°</span>
              </label>
            </div>
            <p className="text-[11px] text-amber-200/40 mt-1">Editing <b className="text-amber-200/70">{device}</b> layout. Desktop and mobile keep separate size &amp; positions.</p>
          </div>

          {/* Settings grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* B. Animation settings */}
            <div className="rounded-2xl border border-amber-500/15 bg-black/20 p-4 space-y-3">
              <span className={lbl}>Animation Settings</span>
              <div>
                <label className={lbl}>Name</label>
                <input value={selected.name} onChange={(e) => patchPreset({ name: e.target.value })} className={fieldCls} data-testid="anim-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Occasion</label>
                  <select value={selected.occasion} onChange={(e) => patchPreset({ occasion: e.target.value })} className={fieldCls}>
                    {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Direction</label>
                  <select value={selected.direction} onChange={(e) => applyDirection(e.target.value)} className={fieldCls} data-testid="anim-direction">
                    {DIRECTIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={lbl}>Duration</label>
                  <select value={selected.duration} onChange={(e) => patchPreset({ duration: Number(e.target.value) })} className={fieldCls} data-testid="anim-duration">
                    {[3, 4, 5, 6, 8, 10].map((s) => <option key={s} value={s}>{s}s</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Entrance</label>
                  <select value={selected.entrance} onChange={(e) => patchPreset({ entrance: e.target.value })} className={fieldCls}>
                    {["fade", "zoom", "none"].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Exit</label>
                  <select value={selected.exit} onChange={(e) => patchPreset({ exit: e.target.value })} className={fieldCls}>
                    {["fade", "zoom", "none"].map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={lbl}>Opacity — {Math.round(selected.opacity * 100)}%</label>
                <input type="range" min="0.2" max="1" step="0.05" value={selected.opacity} onChange={(e) => patchPreset({ opacity: Number(e.target.value) })} className="w-full" />
              </div>
              {selected.direction === "custom" && (
                <div className="flex items-center gap-2">
                  <button className={ghostBtn} onClick={() => { const path = [...(geo.path || [])]; const a = path[path.length - 2] || { x: 50, y: 50 }; const b = path[path.length - 1] || { x: 60, y: 60 }; path.splice(path.length - 1, 0, { x: Math.round((a.x + b.x) / 2), y: Math.round((a.y + b.y) / 2) }); patchGeo({ path }); }} data-testid="anim-add-point"><Plus className="w-4 h-4" /> Add point</button>
                  <span className="text-xs text-amber-200/50">{geo.path?.length || 0} points</span>
                </div>
              )}
            </div>

            {/* Effects + asset */}
            <div className="rounded-2xl border border-amber-500/15 bg-black/20 p-4 space-y-3">
              <span className={lbl}>Character &amp; Effects</span>
              <div>
                <label className={lbl}>Asset (PNG · GIF · WebP · APNG · WebM · MP4 · Lottie)</label>
                <div className="flex items-center gap-2 mt-1">
                  <input value={selected.asset?.url || ""} onChange={(e) => patchNested("asset", { url: e.target.value, type: detectType(null, e.target.value) })} placeholder="/anim/… or paste URL" className={`${fieldCls} flex-1`} data-testid="anim-asset-url" />
                  <button onClick={() => fileRef.current?.click()} className={ghostBtn} data-testid="anim-upload-asset"><UploadCloud className="w-4 h-4" /></button>
                  <input ref={fileRef} type="file" accept="image/*,video/*,.json,.lottie" className="hidden" onChange={(e) => uploadAsset(e.target.files?.[0])} />
                </div>
                <div className="text-[10px] text-amber-200/40 mt-1">Type: {selected.asset?.type || "image"}</div>
              </div>
              <div className="flex items-center justify-between"><span className="text-sm flex items-center gap-2"><Sun className="w-4 h-4 text-amber-400" /> Glow</span><Toggle on={selected.effects.glow > 0} onClick={() => patchNested("effects", { glow: selected.effects.glow > 0 ? 0 : 0.6 })} testid="anim-fx-glow" /></div>
              {selected.effects.glow > 0 && (
                <div className="flex items-center gap-2 pl-6">
                  <input type="range" min="0.1" max="1" step="0.05" value={selected.effects.glow} onChange={(e) => patchNested("effects", { glow: Number(e.target.value) })} className="flex-1" />
                  <input type="color" value={selected.effects.glow_color} onChange={(e) => patchNested("effects", { glow_color: e.target.value })} className="h-7 w-9 rounded bg-transparent" />
                </div>
              )}
              <div className="flex items-center justify-between"><span className="text-sm flex items-center gap-2"><Wind className="w-4 h-4 text-amber-400" /> Smoke trail</span><Toggle on={selected.effects.smoke} onClick={() => patchNested("effects", { smoke: !selected.effects.smoke })} testid="anim-fx-smoke" /></div>
              <div className="flex items-center justify-between"><span className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> Sparkles</span>
                <div className="flex items-center gap-2">{selected.effects.sparkles && <input type="color" value={selected.effects.sparkle_color} onChange={(e) => patchNested("effects", { sparkle_color: e.target.value })} className="h-7 w-9 rounded bg-transparent" />}<Toggle on={selected.effects.sparkles} onClick={() => patchNested("effects", { sparkles: !selected.effects.sparkles })} testid="anim-fx-sparkles" /></div>
              </div>
              <div className="flex items-center justify-between"><span className="text-sm">Shadow</span><Toggle on={selected.effects.shadow} onClick={() => patchNested("effects", { shadow: !selected.effects.shadow })} testid="anim-fx-shadow" /></div>
              <div className="flex items-center justify-between border-t border-amber-500/10 pt-3"><span className="text-sm flex items-center gap-2"><Volume2 className="w-4 h-4 text-amber-400" /> Sound (muted until visitor enables)</span><Toggle on={selected.sound.enabled} onClick={() => patchNested("sound", { enabled: !selected.sound.enabled })} testid="anim-fx-sound" /></div>
              {selected.sound.enabled && (
                <div className="flex items-center gap-2 pl-6">
                  <button onClick={() => soundRef.current?.click()} className={ghostBtn}><UploadCloud className="w-4 h-4" /> Sound</button>
                  <input ref={soundRef} type="file" accept="audio/*" className="hidden" onChange={(e) => uploadSound(e.target.files?.[0])} />
                  <input type="range" min="0" max="1" step="0.05" value={selected.sound.volume} onChange={(e) => patchNested("sound", { volume: Number(e.target.value) })} className="flex-1" />
                </div>
              )}
            </div>
          </div>

          {/* Display + Scheduling */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-amber-500/15 bg-black/20 p-4 space-y-3">
              <span className={lbl}>Website Display</span>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Pages</label>
                  <select value={selected.pages} onChange={(e) => patchPreset({ pages: e.target.value })} className={fieldCls}>
                    <option value="home">Homepage only</option>
                    <option value="all">All pages</option>
                  </select>
                </div>
                <div>
                  <label className={lbl}>Devices</label>
                  <select value={selected.devices} onChange={(e) => patchPreset({ devices: e.target.value })} className={fieldCls}>
                    <option value="all">All devices</option>
                    <option value="desktop">Desktop only</option>
                    <option value="mobile">Mobile only</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-amber-500/15 bg-black/20 p-4 space-y-3">
              <span className={lbl}><span className="inline-flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Scheduling &amp; Frequency</span></span>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>Activation</label>
                  <select value={draft.schedule.mode} onChange={(e) => setDraft((d) => ({ ...d, schedule: { ...d.schedule, mode: e.target.value } }))} className={fieldCls} data-testid="anim-schedule-mode">
                    <option value="manual">Manual (ON/OFF only)</option>
                    <option value="scheduled">Scheduled window</option>
                  </select>
                </div>
                <div>
                  <label className={lbl}>Show to visitor</label>
                  <select value={draft.frequency} onChange={(e) => setDraft((d) => ({ ...d, frequency: e.target.value }))} className={fieldCls} data-testid="anim-frequency">
                    {FREQ.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </div>
              </div>
              {draft.schedule.mode === "scheduled" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={lbl}>Start date</label><input type="date" value={draft.schedule.start_date} onChange={(e) => setDraft((d) => ({ ...d, schedule: { ...d.schedule, start_date: e.target.value } }))} className={fieldCls} /></div>
                  <div><label className={lbl}>End date</label><input type="date" value={draft.schedule.end_date} onChange={(e) => setDraft((d) => ({ ...d, schedule: { ...d.schedule, end_date: e.target.value } }))} className={fieldCls} /></div>
                  <div><label className={lbl}>Start time</label><input type="time" value={draft.schedule.start_time} onChange={(e) => setDraft((d) => ({ ...d, schedule: { ...d.schedule, start_time: e.target.value } }))} className={fieldCls} /></div>
                  <div><label className={lbl}>End time</label><input type="time" value={draft.schedule.end_time} onChange={(e) => setDraft((d) => ({ ...d, schedule: { ...d.schedule, end_time: e.target.value } }))} className={fieldCls} /></div>
                  <p className="col-span-2 text-[11px] text-amber-200/40">Timezone: {draft.timezone}. Manual OFF always overrides the schedule.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
